import { AppBar, makeStyles, styled } from "@material-ui/core";
import { Linked, useIO, useLinked, useModel } from "@type-r/react";
import { Button, Column, Columns, Flex, Loading, Page, SelectSortBy, TilesList, Toolbar } from "lqx-react";
import * as React from "react";
import { useMemo } from "react";
import { PaymentVaultCard } from "../api/paymentVaultCard";
import { PaymentsVaultPage } from "../api/paymentsVaultPage";
import { AddFilter } from "../components/search-bar/search-bar";
import { CountListSection } from "../count-list-section";
import { SortOptions } from "./models/payments-filters";
import { PaymentsStore } from "./models/payments-store";
import { PaymentDetailsPane } from "./payment-details-pane";
import { PaymentVaultCardRenderer } from "./payment-vault-card-renderer";

export const PaymentsPage = () => {
    var classes = useStyles();
    const state = useModel(PaymentsStore);
    const [isReady, setIsReady] = React.useState(false);
    const paymentsVaultPage = useModel(PaymentsVaultPage);
    const $activePaymentCard: Linked<PaymentVaultCard> = useLinked<PaymentVaultCard>(null);
    const $selectedPaymentDetailsTab = useLinked("details");
    const [paymentsVaultCard, setPaymentsVaultCard] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [countAll, setCountAll] = React.useState(0);

    useIO(async () => {
        await Promise.all([
            state.payors.fetch(),
            state.payees.fetch()
        ])

        state.initialize()
        await getData();
        setPaymentsVaultCard(paymentsVaultPage.payments.items.models);
        setCountAll(paymentsVaultPage.payments.count);
    }, [])

    const getData = async (skip = 0, count = 100) => {
        const params = state.filter.mapToQuery(skip, count);
        setIsLoading(true)
        await paymentsVaultPage.payments.fetch({ params: params }).catch(e => console.error(e));
        setIsLoading(false);
        setIsReady(true);
    };

    useMemo(async () => {
        if (isReady) {
            setIsReady(false);
            $activePaymentCard.set(null);
            await getData();
            setPaymentsVaultCard(paymentsVaultPage.payments.items.models);
        }
    }, [state.filter['_changeToken']]);

    const closeDetailsPane = () => $activePaymentCard.set(null);

    const onVaultCardClicked = () => {
        $selectedPaymentDetailsTab.set('details');
    }

    const handleDataOnScroll = async () => {
        if (!isLoading && paymentsVaultPage.payments.items.length === 100) {
            await getData(paymentsVaultCard.length);
            setPaymentsVaultCard(prev => [...prev, ...paymentsVaultPage.payments.items.models]);
        }
    }

    const scrollEvent = (handle: () => void) => ({ target }) => {
        const isBottom = target.scrollHeight - target.offsetHeight - target.scrollTop <= 1 && target.scrollHeight / target.offsetHeight > 2.1;
        if (isBottom) {
            handle();
        }
    }

    return <>
        <Page maxWidth={false} header={
            <Flex center>
                <h4 className={classes.title}>Payments</h4>
            </Flex>
        }
            top={
                <AppBar position="static" color="secondary">
                    <Toolbar>
                        <StyledFilterBar filter={state.filter} filterAllPlaceholder="Filter by Payor, Payee, Currency, Amount, or Value Date (yyyy-mm-dd)" />
                        {/* <StateFilter stateValue={ state.filter.$.stateId } filterOptions={ PaymentStateOptions } /> */}
                        <SelectSortBy $attr={state.filter.sortBy.$.attr} $asc={state.filter.sortBy.$.asc} options={SortOptions} />
                    </Toolbar>
                </AppBar>
            }>

            {
                isReady ?
                    <Columns style={{ minHeight: 'calc(100% - 80px)' }} onScroll={scrollEvent(handleDataOnScroll)}>
                        <TilesListColumn>
                            <CountListSection countAll={countAll} countFiltered={paymentsVaultPage.payments.count}></CountListSection>
                            <TilesList
                                items={paymentsVaultCard}
                                itemSize={100}
                                renderer={PaymentVaultCardRenderer($activePaymentCard, (vaultCard: PaymentVaultCard) => {
                                    return (
                                        <>
                                            {
                                                !$activePaymentCard.value && true/* Can(UserEntitlementCatalogs.EditVaultInvoicesPage) */ && <EditButton onClick={evt => {
                                                    $selectedPaymentDetailsTab.set("edit");
                                                    $activePaymentCard.set(vaultCard);
                                                    evt.stopPropagation();
                                                }} color="primary">Edit</EditButton>
                                            }
                                            {
                                                vaultCard.dataset.isDigitized && <SendToButton color="primary"
                                                    onClick={
                                                        evt => {
                                                            $selectedPaymentDetailsTab.set("sendTo");
                                                            $activePaymentCard.set(vaultCard);
                                                            evt.stopPropagation();
                                                        }
                                                    }>Send To</SendToButton>
                                            }
                                        </>
                                    )
                                }, onVaultCardClicked)}
                                selected={undefined}
                            />
                        </TilesListColumn>
                        {$activePaymentCard.value &&
                            <PaymentDetailsPaneColumn>
                                <PaymentDetailsPane close={closeDetailsPane} payment={$activePaymentCard.value.dataset} paymentId={$activePaymentCard.value.id} $selectedTab={$selectedPaymentDetailsTab} paymentData={$activePaymentCard.value}/>
                            </PaymentDetailsPaneColumn>}
                    </Columns> : <Loading />
            }
        </Page>
    </>
};

const useStyles = makeStyles({
    title: {
        padding: '20px 0',
        color: '#3d6889',
        fontSize: '24px'
    }
});

const TilesListColumn = styled(Column)({
    minWidth: '500px',
    minHeight: '250px',
});

const SendToButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center',
    disabled: 'true'
});

const StyledFilterBar = styled(AddFilter)({
    width: '650px',
    alignItems: 'center',
    padding: '4px',
})

const PaymentDetailsPaneColumn = styled(Column)({
    minWidth: '250px'
});

const EditButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});
